/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

interface NavigationEntryLike {
	id: string;
	url?: string;
}

interface NavigationLike {
	currentEntry: NavigationEntryLike | null;
	addEventListener(type: string, listener: (event: Event) => void): void;
}

const getNav = (): NavigationLike | null => {
	if (typeof window === 'undefined') return null;
	const nav = (window as unknown as {navigation?: NavigationLike}).navigation;
	return nav ?? null;
};

const STATE_KEY = '__fluxer_scroll_key';
const scrollMap = new Map<string, {x: number; y: number}>();
let installed = false;

const getKey = (): string => {
	const nav = getNav();
	if (nav?.currentEntry) return `n:${nav.currentEntry.id}`;
	const state = window.history.state as Record<string, unknown> | null;
	let key = state && typeof state[STATE_KEY] === 'string' ? (state[STATE_KEY] as string) : null;
	if (!key) {
		key = `h:${Math.random().toString(36).slice(2)}`;
		try {
			window.history.replaceState({...(state ?? {}), [STATE_KEY]: key}, '', window.location.href);
		} catch {
			/* ignore */
		}
	}
	return key;
};

const snapshot = () => {
	if (typeof window === 'undefined') return;
	scrollMap.set(getKey(), {x: window.scrollX, y: window.scrollY});
};

const restore = () => {
	if (typeof window === 'undefined') return;
	const pos = scrollMap.get(getKey());
	if (!pos) return;
	requestAnimationFrame(() => window.scrollTo(pos.x, pos.y));
};

/**
 * Install per-entry window-scroll restoration. Idempotent; safe to call
 * multiple times. Service-worker / message-list internal scroll containers
 * are left to their own state — this only handles the window scroll.
 */
export function installScrollRestoration(): void {
	if (installed || typeof window === 'undefined') return;
	installed = true;
	window.history.scrollRestoration = 'manual';

	const nav = getNav();
	if (nav) {
		nav.addEventListener('navigate', () => snapshot());
		nav.addEventListener('navigatesuccess', () => restore());
		return;
	}

	const onPopState = () => {
		// Snapshot fires too late on popstate (current entry is already the new
		// one), so the snapshot must happen before the navigation. We snapshot
		// on every scroll-stop instead.
		restore();
	};
	let scrollTimer: number | null = null;
	const onScroll = () => {
		if (scrollTimer !== null) window.clearTimeout(scrollTimer);
		scrollTimer = window.setTimeout(snapshot, 100);
	};
	window.addEventListener('popstate', onPopState);
	window.addEventListener('scroll', onScroll, {passive: true});
}
